import { useEffect, useState } from "react";
import { Sparkles, CalendarClock, Server, ArrowUpCircle, AlertCircle } from "lucide-react";
import api from "../lib/api";
import ContactSupportModal from "./ContactSupportModal";

const STATUS_STYLE = {
  active: "text-[#00E676] bg-[#00E676]/10",
  lifetime: "text-[#00F0FF] bg-[#00F0FF]/10",
  inactive: "text-gray-400 bg-white/5",
  suspended: "text-[#FF3B30] bg-[#FF3B30]/10",
  expired: "text-yellow-400 bg-yellow-400/10",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

/** Reusable Subscription card. Used on Investor Dashboard + Subscription page. */
export default function SubscriptionCard({ compact = false, onOpenSupport }) {
  const [snap, setSnap] = useState(null);
  const [modal, setModal] = useState(false);

  const load = () => api.get("/subscription/me").then(({ data }) => setSnap(data)).catch(() => setSnap({}));
  useEffect(() => { load(); }, []);

  if (!snap) return <div className="card-dark p-6 skeleton h-40" />;

  const noSub = !snap.has_subscription;
  const st = snap.status || "inactive";
  const isLifetime = snap.is_lifetime || st === "lifetime";
  const openSupport = () => onOpenSupport ? onOpenSupport() : setModal(true);

  return (
    <div className="card-dark p-6" data-testid="subscription-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="label-xs">Current Subscription</p>
          <div className="flex items-center gap-3 mt-2">
            <h3 className="text-xl text-white font-medium" data-testid="subscription-plan-name">
              {noSub ? "No active plan" : snap.plan_name}
            </h3>
            <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${STATUS_STYLE[st] || STATUS_STYLE.inactive}`} data-testid="subscription-status">
              {st}
            </span>
          </div>
          {!noSub && snap.price_display && !isLifetime && (
            <p className="text-sm text-gray-500 mt-1">{snap.price_display}/{snap.plan_code === "ENTERPRISE" ? "custom" : "month"}</p>
          )}
        </div>
        <Sparkles className="w-5 h-5 text-[#7000FF]" />
      </div>

      {snap.pending_request && (
        <div className="flex items-start gap-2 bg-[#FFD600]/10 border border-[#FFD600]/30 rounded px-3 py-2 mb-4" data-testid="subscription-pending-request">
          <AlertCircle className="w-4 h-4 text-[#FFD600] mt-0.5" />
          <p className="text-xs text-yellow-100">
            Upgrade request <span className="font-mono">{snap.pending_request.request_id}</span> is pending admin review.
            Access is granted only after an admin approves it.
          </p>
        </div>
      )}

      {noSub && (
        <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded px-3 py-2 mb-4">
          <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
          <p className="text-xs text-yellow-100">
            You don't have a subscription yet. Contact support to activate a plan and start connecting MT5 accounts.
          </p>
        </div>
      )}

      {!compact && !noSub && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <Metric icon={Server} label="MT5 Limit" value={snap.mt5_limit_display} testId="sub-mt5-limit" />
          <Metric icon={Server} label="MT5 Used" value={snap.mt5_used} testId="sub-mt5-used" />
          <Metric icon={Server} label="Remaining" value={snap.mt5_remaining_display} testId="sub-mt5-remaining" />
          {!isLifetime && (
            <>
              <Metric icon={CalendarClock} label="Start" value={fmtDate(snap.start_date)} />
              <Metric icon={CalendarClock} label="Renewal" value={fmtDate(snap.renewal_date)} testId="sub-renewal" />
              <Metric icon={CalendarClock} label="Expires" value={fmtDate(snap.expiry_date)} testId="sub-expiry" />
            </>
          )}
          {isLifetime && (
            <Metric icon={CalendarClock} label="Access" value="Lifetime" />
          )}
        </div>
      )}

      {compact && !noSub && (
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div><p className="tabular text-lg text-white" data-testid="sub-mt5-used-compact">{snap.mt5_used}</p><p className="text-[10px] text-gray-500 uppercase tracking-widest">Used</p></div>
          <div><p className="tabular text-lg text-[#00F0FF]" data-testid="sub-mt5-limit-compact">{snap.mt5_limit_display}</p><p className="text-[10px] text-gray-500 uppercase tracking-widest">Limit</p></div>
          <div><p className="tabular text-lg text-[#00E676]" data-testid="sub-mt5-remaining-compact">{snap.mt5_remaining_display}</p><p className="text-[10px] text-gray-500 uppercase tracking-widest">Remaining</p></div>
        </div>
      )}

      <button
        onClick={openSupport}
        data-testid="upgrade-plan-button"
        className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2">
        <ArrowUpCircle size={16} /> {noSub ? "Activate Plan" : "Upgrade Plan"}
      </button>

      {!onOpenSupport && (
        <ContactSupportModal open={modal} onClose={() => { setModal(false); load(); }} />
      )}
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, testId }) => (
  <div>
    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
      {Icon && <Icon size={11} />}
      <p className="text-[10px] uppercase tracking-widest">{label}</p>
    </div>
    <p className="text-sm text-white tabular" data-testid={testId}>{value ?? "—"}</p>
  </div>
);
